import moment from "moment";
import { useTranslation } from "react-i18next";
import React from "react";
import ReactTooltip from "react-tooltip";
import styled from "styled-components";
import useSWR from "swr";
import { ImageAttributePreview } from "../../../../components/AttributeGroupFormGenerator/ImageAttributePreview";
import { DocumentAttributePreview } from "../../../../components/AttributeGroupFormGenerator/DocumentAttribute/DocumentAttributePreview";
import { DocumentMoreModal } from "../../../../components/AttributeGroupFormGenerator/DocumentAttribute/DocumentMoreModal";
import type { DocumentCollectionValue } from "../../../../util/collectionAttributeDisplayValue";
import { useAuthContext } from "../../../../components/Auth";
import { ErrorPlaceholder } from "../../../../components/Error";
import {
  ImagePlaceHolderIcon,
  LoadingIcon,
} from "../../../../components/Icons/Icons";
import { ToggleSwitchV2 } from "../../../../components/ToggleSwitch/ToggleSwitch";
import { endpoints } from "../../../../endpoints";
import { TwoColumGrid } from "../../../../layout/portalPageLayout";
import type {
  Assets,
  AssetsSummary,
  AttributeSchema,
  AttributeValue,
  DocumentAttributeValue,
  ImageAttributeValue,
  IobjectReferenceCollectionItem,
  IproductReferenceCollectionItem,
  PIMProduct,
} from "../../../../types/types.PIM";
import { useRoutePath } from "../../../../util/Routing";
import {
  getUserRefTooltip,
  getUserName,
  useStoreState,
} from "../../../../util/util";
import { LinkAttributeValueSchema } from "../../../../util/zod.util";
import type { OptionType, User } from "../../../../types/types";
import { Chips } from "../../../../components/Chips/Chips";
import { TooltipPortal } from "../../../../components/TooltipModal/TooltipPortal";
import { ObjectReferenceAttributeDisplay } from "../../../../components/ObjectReference/ObjectReferenceAttributeDisplay";
import { ProductReferenceAttributeDisplay } from "../../../../components/ProductReference/ProductReferenceAttributeDisplay";
import {
  AddressApiPayload,
  AddressValue,
  transformAddress,
} from "../../../admin/SellerAdmin/PIM/SellerAdminPIMAttributes/AddressAttributes/address.utils";
import { getAddressDisplayLines } from "../../../admin/SellerAdmin/PIM/SellerAdminPIMAttributes/AddressAttributes/addressDisplay.utils";
import { TextSmRegular } from "../../../../components/Typography/TypographyV2";
import { radiusTokens, spacingTokens } from "../../../../designTokens";

const InnerContentText = styled.p<{ backgroundColor?: string }>`
  color: ${({ theme }) => theme.primaryTextColor};
  font-family: ${({ theme }) => theme.fontFamily};
  font-size: ${({ theme }) => theme.fontSizes.medium};
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.33;
  letter-spacing: normal;
  margin: 0;
  ${({ backgroundColor }) =>
    backgroundColor
      ? `
    background-color: ${backgroundColor};
    border-radius: 2px;
    padding: 2px 4px;
    display: inline-block;
  `
      : ""}
`;

const StyledUL = styled.ul<{ backgroundColor?: string }>`
  padding-left: ${({ theme }) => theme.resolve("spacing_md")};
  margin: 5px 0 10px;
  margin-left: 13px;
  li {
    font-size: ${({ theme }) => theme.fontSizes.medium};
    line-height: 1.33;
    padding-bottom: 5px;

    span,
    a {
      margin-left: -2px;
      position: relative;
      ${({ backgroundColor }) =>
        backgroundColor
          ? `
        background-color: ${backgroundColor};
        border-radius: 2px;
        padding: 2px 4px;
      `
          : ""}
    }

    &::marker {
      font-size: ${({ theme }) => theme.fontSizes.regular};
    }
    span {
      margin-left: -2px;
      position: relative;
      ${({ backgroundColor }) =>
        backgroundColor
          ? `
        background-color: ${backgroundColor};
        border-radius: 2px;
        padding: 2px 4px;
      `
          : ""}
    }

    &::marker {
      font-size: ${({ theme }) => theme.fontSizes.regular};
    }
  }
`;

const MultilineWrapper = styled.div<{ line_clamp: number }>`
  p {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: ${({ line_clamp }) => line_clamp};
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: pointer;
  }
`;

const LoadingIconContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
`;

const renderIconWithText = (
  value: AttributeValue,
  size: "small" | "medium" | "large"
) => {
  const imageSize = size === "small" ? 32 : size === "medium" ? 64 : 128;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: size === "large" ? "column" : "row",
        alignItems: size === "small" || size === "medium" ? "center" : "unset",
        justifyContent: "flex-start",
      }}
    >
      {value.enum_variant?.thumbnail ? (
        <img
          src={value.enum_variant?.thumbnail}
          alt="attribute-icon"
          width={imageSize}
          height={imageSize}
          style={{
            marginRight: size !== "large" ? "10px" : "0",
            marginBottom: size === "large" ? "10px" : "0",
            borderRadius: "3px",
          }}
        />
      ) : (
        <div
          style={{
            marginRight: size !== "large" ? "10px" : "0",
            marginBottom: size === "large" ? "10px" : "0",
          }}
        >
          <ImagePlaceHolderIcon width={imageSize} height={imageSize} />
        </div>
      )}
      <div>{value.enum_variant?.variant}</div>
    </div>
  );
};

const ImageAttributeDisplay = ({
  attribute,
  isPortfolio,
}: {
  attribute: AttributeSchema;
  isPortfolio: boolean;
}) => {
  const { tenant_id } = useStoreState();
  const { t } = useTranslation();

  const { data: asset, error: asset_error } = useSWR<AssetsSummary>(
    attribute.values?.length
      ? endpoints.v2_tenants_id_pim_assets_id_summary(
          tenant_id,
          (attribute.values[0].value as ImageAttributeValue).asset_id
        )
      : null
  );

  return (
    <>
      {!asset_error && !asset && (
        <LoadingIconContainer>
          <LoadingIcon width={24} height={24} />
        </LoadingIconContainer>
      )}
      {asset_error && <ErrorPlaceholder message={t("Could not load image")} />}
      {asset && (
        <ImageAttributePreview
          attribute={attribute}
          asset={asset as Assets}
          is_portfolio_page={isPortfolio}
        />
      )}
    </>
  );
};

export const AttributeValueDisplay = ({
  attribute,
  isPortfolio,
  max_line_clamp,
  backgroundColor,
}: {
  attribute: AttributeSchema;
  isPortfolio: boolean;
  max_line_clamp?: number;
  backgroundColor?: string;
}) => {
  const { storePath, adminPath } = useRoutePath();
  const { t } = useTranslation();
  const { roleIsSellerAdmin, user } = useAuthContext();
  const [documentMoreDocs, setDocumentMoreDocs] =
    React.useState<{
      docs: DocumentCollectionValue[];
      attributeName: string;
    } | null>(null);

  if (attribute.is_not_applicable) {
    return <InnerContentText>--</InnerContentText>;
  }

  switch (attribute.input_type) {
    case "single_select":
      if (attribute.icons_enabled && attribute.values?.length) {
        return renderIconWithText(
          attribute.values[0],
          attribute.size ?? "medium"
        );
      }
      return (
        <InnerContentText backgroundColor={backgroundColor}>
          {attribute.values?.length && attribute.values[0].value !== " "
            ? (attribute.values[0].value as string)
            : "--"}
        </InnerContentText>
      );
    case "numeric":
      return (
        <InnerContentText backgroundColor={backgroundColor}>
          {attribute.values?.length && attribute.values[0].value !== " "
            ? (attribute.values[0].value as string)
            : "--"}
        </InnerContentText>
      );
    case "date":
      return (
        <InnerContentText backgroundColor={backgroundColor}>
          {attribute.values?.length && attribute.values[0].value !== " "
            ? moment(attribute.values[0].value as string)?.format(
                "MMM DD, YYYY"
              )
            : "--"}
        </InnerContentText>
      );
    case "form_field":
      return (
        <InnerContentText backgroundColor={backgroundColor}>
          {attribute.values?.length && attribute.values[0].value !== " "
            ? (attribute.values[0].value as string)
            : "--"}
        </InnerContentText>
      );
    case "multiline_entry":
      return (
        <>
          {attribute.values?.length ? (
            <>
              {max_line_clamp ? (
                <MultilineWrapper
                  line_clamp={max_line_clamp}
                  dangerouslySetInnerHTML={{
                    __html:
                      `<div class="ql-editor">${
                        attribute.values[0].value as string
                      }</div>` || "--",
                  }}
                />
              ) : (
                <div
                  dangerouslySetInnerHTML={{
                    __html:
                      `<div class="ql-editor">${
                        attribute.values[0].value as string
                      }</div>` || "--",
                  }}
                />
              )}
            </>
          ) : (
            <InnerContentText>--</InnerContentText>
          )}
        </>
      );
    case "toggle":
      return (
        <>
          <ToggleSwitchV2
            label={attribute.display_name ? t([attribute.display_name]) : ""}
            name={attribute.name}
            checked={
              attribute.values?.length
                ? (attribute.values[0].value as boolean)
                : false
            }
            key={attribute.id}
            disabled={true}
          />
        </>
      );
    case "multi_select":
      const size = attribute.size;
      const imageSize = size === "small" ? 32 : size === "medium" ? 64 : 128;

      const itemWidth = size === "large" ? imageSize : `calc(33.33% - 10px)`;

      if (attribute.values?.length && !attribute.icons_enabled) {
        return (
          <TwoColumGrid>
            <StyledUL backgroundColor={backgroundColor}>
              {attribute.values.map((value, index) => {
                return (
                  <li key={value.id}>
                    <span>{value.enum_variant?.variant}</span>
                  </li>
                );
              })}
            </StyledUL>
          </TwoColumGrid>
        );
      }

      return attribute.values?.length && attribute.icons_enabled ? (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            gap: "15px",
            justifyContent: "flex-start",
          }}
        >
          {attribute.values.map((value) => (
            <div key={value.id} style={{ width: itemWidth }}>
              {renderIconWithText(value, size)}
            </div>
          ))}
        </div>
      ) : (
        <InnerContentText>--</InnerContentText>
      );
    case "link":
      const parsed = LinkAttributeValueSchema.safeParse(
        attribute.values?.[0]?.value
      );

      const linkStyle = backgroundColor
        ? {
            backgroundColor: backgroundColor,
            borderRadius: radiusTokens.radius_xxs,
            padding: `${spacingTokens.spacing_xxs} ${spacingTokens.spacing_xs}`,
          }
        : undefined;

      if (parsed.success && parsed.data.url) {
        return (
          <InnerContentText>
            <a
              href={parsed.data.url}
              target={"_blank"}
              rel="noreferrer"
              style={linkStyle}
            >
              {parsed.data.display_text}
            </a>
          </InnerContentText>
        );
      } else if (
        parsed.success &&
        !parsed.data.url &&
        parsed.data.display_text !== ""
      ) {
        return linkStyle ? (
          <InnerContentText>
            <span style={linkStyle}>{parsed.data.display_text}</span>
          </InnerContentText>
        ) : (
          <InnerContentText>{parsed.data.display_text}</InnerContentText>
        );
      } else return <InnerContentText>--</InnerContentText>;
    case "product_reference":
      return (
        <ProductReferenceAttributeDisplay
          attribute={attribute}
          isPortfolio={isPortfolio}
          storePath={storePath}
          adminPath={adminPath}
          user={user}
          roleIsSellerAdmin={roleIsSellerAdmin}
          backgroundColor={backgroundColor}
          t={t}
        />
      );
    case "user":
      if (attribute.is_multiple_choice && attribute.values?.length) {
        return (
          <Chips
            key={attribute.id}
            chips={attribute.values.map((value) => ({
              name: getUserName(value.user?.firstname, value.user?.lastname),
              active: true,
              dataTip: value.user ? getUserRefTooltip(value.user as User) : "",
              dataHtml: true,
            }))}
            handleClick={() => {}}
            selectionMode="many"
            header=""
            errors={{}}
            name="users"
          />
        );
      }
      return attribute.values?.length ? (
        <StyledUL backgroundColor={backgroundColor}>
          {attribute.values.map((value) => {
            return (
              <li key={value.id}>
                <span
                  data-tip={
                    value.user ? getUserRefTooltip(value.user as User) : ""
                  }
                  data-for={value.user?.id}
                  data-html={true}
                >
                  {getUserName(value.user?.firstname, value.user?.lastname)}
                </span>
                <TooltipPortal>
                  <ReactTooltip
                    id={value.user?.id}
                    place="top"
                    data-html={true}
                    effect="solid"
                    multiline={true}
                  />
                </TooltipPortal>
              </li>
            );
          })}
        </StyledUL>
      ) : (
        <InnerContentText>--</InnerContentText>
      );
    case "image":
      return attribute.values?.length ? (
        <ImageAttributeDisplay
          attribute={attribute}
          isPortfolio={isPortfolio}
        />
      ) : (
        <InnerContentText>--</InnerContentText>
      );

    case "document":
      return attribute.values?.length ? (
        <>
          <DocumentAttributePreview
            attribute={attribute}
            maxVisible={1}
            onMoreClick={(vals) =>
              setDocumentMoreDocs({
                docs: (vals ?? []).map((av) => ({
                  ...(av.value as DocumentAttributeValue),
                  url: av.cdn_url ?? av.signed_url ?? av.preview_url,
                })),
                attributeName: attribute.display_name || attribute.name,
              })
            }
          />
          {documentMoreDocs && (
            <DocumentMoreModal
              docs={documentMoreDocs.docs}
              attributeName={documentMoreDocs.attributeName}
              onClose={() => setDocumentMoreDocs(null)}
            />
          )}
        </>
      ) : (
        <InnerContentText>--</InnerContentText>
      );

    case "address": {
      const addressValue = attribute.values?.[0]?.value as
        | Record<string, any>
        | undefined;
      if (!addressValue) return <InnerContentText>--</InnerContentText>;

      const { postalLines, metaFields } = getAddressDisplayLines(
        addressValue,
        t
      );

      if (!postalLines.length && !metaFields.length) {
        return <InnerContentText>--</InnerContentText>;
      }

      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {/* Postal block — each part on its own line */}
          {postalLines.map((line, i) => (
            <InnerContentText key={i}>{line}</InnerContentText>
          ))}

          {/* Meta fields rendered as "Label: Value" brlow */}
          {metaFields.length > 0 && (
            <div
              style={{
                marginTop: 10,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              {metaFields.map(({ label, value }) => (
                <InnerContentText key={label}>
                  <TextSmRegular style={{ opacity: 0.6 }}>
                    {label}:
                  </TextSmRegular>{" "}
                  <TextSmRegular>{value}</TextSmRegular>
                </InnerContentText>
              ))}
            </div>
          )}
        </div>
      );
    }
    case "object_reference": {
      return (
        <ObjectReferenceAttributeDisplay
          attribute={attribute}
          isPortfolio={isPortfolio}
          storePath={storePath}
          adminPath={adminPath}
          user={user}
          roleIsSellerAdmin={roleIsSellerAdmin}
          backgroundColor={backgroundColor}
          t={t}
        />
      );
    }
    default:
      return (
        <InnerContentText>
          {attribute.values?.length
            ? (attribute.values[0].value as string)
            : "--"}
        </InnerContentText>
      );
  }
};

export interface ProductGroupFormValues {
  [key: string]:
    | OptionType<string>
    | string
    | string[]
    | boolean
    | number
    | { display_text: string; url: string }
    | ImageAttributeValue
    | DocumentAttributeValue
    | DocumentAttributeValue[]
    | Record<string, any>
    | OptionType<string>[];
}

export const processAttributeFormValue = (
  value: Exclude<
    ProductGroupFormValues[string],
    OptionType<string>[] | string[]
  >,
  input_type: AttributeSchema["input_type"]
) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  switch (input_type) {
    case "date":
      return moment(value as string).format("MMM DD, YYYY");
    case "single_select":
    case "product_reference":
    case "user":
    case "object_reference":
      return (value as OptionType<string>).value as string;
    case "link": {
      const { display_text, url } = value as {
        display_text: string;
        url: string;
      };
      return display_text === "" && url === ""
        ? null
        : (value as { display_text: string; url: string });
    }
    case "toggle":
    case "checkbox":
      return JSON.stringify(value as boolean);

    case "address": {
      if (typeof value !== "object" || Array.isArray(value)) return null;
      return transformAddress(value as AddressValue);
    }
    case "document": {
      if (typeof value !== "object" || Array.isArray(value)) return null;
      const { asset_id } = value as DocumentAttributeValue;
      return asset_id ? { asset_id } : null;
    }
    default:
      return value as string;
  }
};

export const isExistingAttributeFormValue = (
  value: Exclude<
    ProductGroupFormValues[string],
    OptionType<string>[] | string[]
  >,
  existingValue: AttributeValue["value"],
  input_type: AttributeSchema["input_type"]
) => {
  if (
    existingValue === null ||
    existingValue === undefined ||
    existingValue === ""
  ) {
    return false;
  }

  switch (input_type) {
    case "link":
      const existingLinkValue = existingValue as {
        url: string;
        display_text: string;
      };
      return (
        (value as { url: string; display_text: string }).url ===
          existingLinkValue.url &&
        (value as { url: string; display_text: string }).display_text ===
          existingLinkValue.display_text
      );
    case "image":
      const { asset_id, caption, image_dimensions } =
        value as ImageAttributeValue;
      const {
        asset_id: existing_asset_id,
        caption: existing_caption,
        image_dimensions: existing_image_dimensions,
      } = existingValue as ImageAttributeValue;
      return (
        asset_id === existing_asset_id &&
        caption?.trim() === existing_caption?.trim() &&
        image_dimensions === existing_image_dimensions
      );
    case "document": {
      const nextAssetId = (value as DocumentAttributeValue).asset_id;
      const existingAssetId = (existingValue as DocumentAttributeValue)
        .asset_id;
      return nextAssetId === existingAssetId;
    }
    case "toggle":
    case "checkbox":
      return typeof value === "string"
        ? (value === "true") === (existingValue as boolean)
        : value === (existingValue as boolean);
    case "date":
      return value === moment(existingValue as string).format("MMM DD, YYYY");
    case "product_reference": {
      const existingValueProductReference =
        typeof existingValue === "object"
          ? (existingValue as IproductReferenceCollectionItem).id
          : existingValue;
      return (value as OptionType)?.value
        ? (value as OptionType).value === existingValueProductReference
        : value === existingValueProductReference;
    }
    case "user":
      const existingValueUser =
        typeof existingValue === "object"
          ? (existingValue as User).id
          : existingValue;
      return (value as OptionType)?.value
        ? (value as OptionType).value === existingValueUser
        : value === existingValueUser;
    case "object_reference": {
      const existingValueObjectReference =
        typeof existingValue === "object"
          ? (existingValue as IobjectReferenceCollectionItem).id
          : existingValue;
      return (value as OptionType)?.value
        ? (value as OptionType).value === existingValueObjectReference
        : value === existingValueObjectReference;
    }

    case "address": {
      if (
        typeof value !== "object" ||
        Array.isArray(value) ||
        typeof existingValue !== "object"
      ) {
        return false;
      }
      const transformed = transformAddress(value as AddressValue);
      const existingAddr = existingValue as AddressApiPayload;
      const newAddr = transformed ?? {};
      const allKeys = new Set([
        ...Object.keys(newAddr),
        ...Object.keys(existingAddr),
      ]);
      return [...allKeys].every(
        (key) =>
          (newAddr[key as keyof AddressApiPayload] ?? "") ===
          (existingAddr[key as keyof AddressApiPayload] ?? "")
      );
    }
    default:
      return value === existingValue;
  }
};

export const getProductTypePermissionSuffix = (
  pim_product_type: PIMProduct["pim_product_type"]
) => {
  switch (pim_product_type) {
    case "master":
      return "product_type_master";
    case "variant":
      return "product_type_variant";
    case "standard":
    default:
      return "product_type_standard";
  }
};
